/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useCallback, useMemo } from "react";
import { Button } from "../../../../components/ui/button";
import { Icon } from "../../../../components/Shared/Icon";
import { useStoreDispatch } from "../../../../store/hooks";
import { setBasicDialogOpenFor, setConfirmDialog } from "../../../../store/helpers/helperReducers";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../../store/helpers/helperSelectors";
import { useSelector } from "react-redux";
import { constStr } from "../../../../globals/constants";
import { prepareCustomEventCellDataGrid } from "../../../../pages/Configuration/helpers";
import CustomDataGrid from "../../../../components/Shared/Table/CustomDataGrid";
import SearchBox from "../../../../components/Shared/SearchBox/SearchBox";
import ConfirmDialog from "../../../../components/Shared/Dialogs/ConfirmDialog";
import { useDisplayCustomEvents, useAddEditDeleteCustomEvent } from "./hooks";
import AddEditCustomEvent from "./AddEditCustomEvent";
import { ICustomEvent } from "../../../../store/api/admin/types";
import { FilterX, MoreVertical, Pencil, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import { GridColDef } from "@mui/x-data-grid";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../components/ui/tooltip";
import ActiveFilterChips from "../../../../components/Shared/Filter/ActiveFilterChips";
import { resetKeywordForScope } from "../../../../store/search/reducer";
import { searchKeywordByScopeSelector } from "../../../../store/search/selector";

const CUSTOM_EVENTS_SEARCH_SCOPE = "custom_events_grid_search";

const CustomEvents: React.FC = () => {

  const dispatch = useStoreDispatch();
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);
  const searchKeyword = useSelector(searchKeywordByScopeSelector(CUSTOM_EVENTS_SEARCH_SCOPE));
  const [modelOpenMode, setModelOpenMode] = React.useState("Add");

  const { data, isLoading, customEventsColumns } = useDisplayCustomEvents();
  const {
    setFormData,
    isCreateCustomEvent,
    setisCreateCustomEvent,
    selectedCustomEvent,
    setSelectedCustomEvent,
    handleDeleteCustomEvent,
  } = useAddEditDeleteCustomEvent();
  const rowsData = prepareCustomEventCellDataGrid(data) || [];
  const filteredRows = useMemo(() => {
    if (!searchKeyword?.trim()) {
      return rowsData;
    }
    const keyword = searchKeyword.toLowerCase();
    return rowsData.filter((item: any) =>
      item?.name?.toLowerCase()?.includes(keyword) ||
      item?.description?.toLowerCase()?.includes(keyword) ||
      item?.matchString?.toLowerCase()?.includes(keyword)
    );
  }, [rowsData, searchKeyword]);
  const noOfRows = filteredRows?.length || 0;
  const showReset = !!searchKeyword?.trim();

  const handleClearFilters = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: CUSTOM_EVENTS_SEARCH_SCOPE }));
  }, [dispatch]);

  const handleClearSearch = useCallback(() => {
    dispatch(resetKeywordForScope({ scope: CUSTOM_EVENTS_SEARCH_SCOPE }));
  }, [dispatch]);

  const handleOnAddCustomEvent = () => {
    setisCreateCustomEvent(true);
    setModelOpenMode("Add");
    setSelectedCustomEvent(undefined);
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.CUSTOMEVENT));
  };


  const handleOnEditCustomEvent = (selectedEvent: ICustomEvent) => {
    setisCreateCustomEvent(false);
    setModelOpenMode("Edit");
    setSelectedCustomEvent(selectedEvent);
    setFormData(selectedEvent);
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.CUSTOMEVENT));
  };

  const handleDeleteCusotmEvent = (actionName: string) => {
    if (actionName?.toLowerCase().includes("delete")) {
      handleDeleteCustomEvent(selectedCustomEvent?.name, "delete");
    }
  };

  const handleDeleteRow = (row: ICustomEvent) => {
    setSelectedCustomEvent(row);
    dispatch(setConfirmDialog(DIALOGOPENFOR.CUSTOMEVENT));
  };

  const actionsColumn: GridColDef = {
    field: "actions",
    headerName: "",
    width: 60,
    sortable: false,
    disableColumnMenu: true,
    renderCell: (params) => (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 border-0 bg-transparent rounded-full text-foreground"
            aria-label="Row actions"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              const row = params.row as ICustomEvent;
              setTimeout(() => handleOnEditCustomEvent(row), 200);
            }}
          >
            <Pencil className="h-4 w-4" fill="currentColor" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => {
              const row = params.row as ICustomEvent;
              setTimeout(() => handleDeleteRow(row), 200);
            }}
          >
            <Trash2 className="h-4 w-4" fill="currentColor" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  };

  return (
    <div className="flex flex-col justify-center items-start w-full">
      {openConfirmDialog === DIALOGOPENFOR.CUSTOMEVENT && (
        <ConfirmDialog actionName={constStr.yesDelete} handleClickAction={handleDeleteCusotmEvent}>
          Are you sure you want to delete <strong>{selectedCustomEvent?.name || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      {basicDialogOpenFor === DIALOGOPENFOR.CUSTOMEVENT && <AddEditCustomEvent modelOpenMode={modelOpenMode} selectedCustomEvent={selectedCustomEvent} isCreateCustomEvent={isCreateCustomEvent} />}
      {rowsData.length === 0 ? (
        <div className="flex items-center justify-center w-full mt-3" >
          <div className="flex justify-center">
            <div className="rounded-lg border bg-card p-8 text-center max-w-sm w-full shadow-sm">
              <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                <Icon name="event_note" size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1">No custom events yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Add a custom event to define a name, pattern, and matching options.
              </p>
              <Button type="button" size="sm" onClick={handleOnAddCustomEvent}>
                <Icon name="add" size={16} className="mr-1.5" />
                Add Event
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between flex-wrap gap-x-4 gap-y-2 w-full max-w-full overflow-x-hidden mt-2 px-3">
            <div className="flex items-center flex-wrap gap-2 min-w-0 max-w-full flex-1">
              <div style={{ marginRight: 1 }} className="shrink-0">
                <SearchBox tooltip="Search custom events..." placeholder="Search custom events..." scope={CUSTOM_EVENTS_SEARCH_SCOPE} width={270} />
              </div>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="inline-flex items-center justify-center rounded-md text-sm h-8 w-8 bg-muted transition-colors duration-150 hover:bg-muted"
                    onClick={handleClearFilters}
                    aria-label="Reset All"
                    style={{ visibility: showReset ? "visible" : "hidden", marginLeft: "7px" }}
                  >
                    <FilterX className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Reset All</TooltipContent>
              </Tooltip>
            </div>
            <div className="flex items-center shrink-0 ml-auto">
              <Button variant="outline" onClick={handleOnAddCustomEvent} style={{ backgroundColor: '#004f69', color: '#fff', borderColor: '#004f69' }}>
                <Icon name="add" size={18} />
                Add Event
              </Button>
            </div>
          </div>
          <div className="flex justify-between items-end ml-3 mb-1 mt-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">{`${noOfRows || 0} results`}</span>
              <ActiveFilterChips
                filters={[]}
                searchKeyword={searchKeyword}
                onClearSearch={handleClearSearch}
                onClearAll={handleClearFilters}
                dispatch={dispatch}
              />
            </div>
          </div>

          <div className="w-full pl-3 pr-3">
            <CustomDataGrid
              tableId="customEvent"
              rows={filteredRows}
              columns={[...customEventsColumns, actionsColumn]}
              checkboxSelection={false}
              rowCount={noOfRows}
              loading={isLoading}
              disableColumnMenu={true}
              disableRowSelectionOnClick={true}
              getRowId={(row: ICustomEvent) => row?.name!}
              paginationMode="client"
              sortingMode="client"
              fillContainer
            />
          </div></>

      )}

    </div >
  );
};

export default CustomEvents;
